import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { useToast } from '@/hooks/use-toast';
import { isDealNotEditableError } from '@/lib/utils/deal-error';
import { dealRefetchAtom } from '@/states/deal.state';
import { useAtomValue } from 'jotai';

/**
 * Provides `handleIfDealNotEditable(codeOrResult)`:
 * - If the error is `dealNotEditable`: shows a toast, refetches the deal
 *   (to resync editionMode / isOfferLockedState), and returns `true`.
 * - Otherwise returns `false`.
 */
export function useDealNotEditableHandler() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const dealRefetch = useAtomValue(dealRefetchAtom);

  const handleIfDealNotEditable = useCallback(
    (codeOrResult: unknown): boolean => {
      if (!isDealNotEditableError(codeOrResult)) return false;

      toast({
        title: t('general.error'),
        description: t('domain_errors.dealNotEditable.message'),
        variant: 'destructive',
      });

      // Refetch deal to resync editionMode → isOfferLockedState recalculates
      void dealRefetch.fn();

      return true;
    },
    [t, toast, dealRefetch]
  );

  return { handleIfDealNotEditable };
}
